import React from "react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { cn } from "../../lib/utils";

const META_EVENTS = ["Lead", "Contact", "Schedule", "ViewContent", "CompleteRegistration", "SubmitApplication", "Purchase"];
const TIKTOK_EVENTS = ["ClickButton", "Contact", "SubmitForm", "ViewContent", "CompleteRegistration", "Schedule", "PlaceAnOrder"];

const inputCls = "h-10 rounded-none border-border bg-transparent px-3 text-sm focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0";
const areaCls = "rounded-none border-border bg-transparent px-3 py-2 text-sm focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0";

const F = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">{label}</Label>
    {children}
    {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
  </div>
);

const PlatformCard = ({ title, subtitle, enabled, onToggle, testId, children, badge }) => (
  <div className={cn("border bg-background transition-colors", enabled ? "border-primary/50" : "border-border")} data-testid={testId}>
    <div className="flex items-center justify-between gap-4 border-b border-border px-4 py-3">
      <div className="flex items-center gap-3">
        <span className={cn("flex h-8 w-8 items-center justify-center border text-[10px] font-bold", enabled ? "border-primary text-primary" : "border-border text-muted-foreground")}>{badge}</span>
        <div>
          <p className="text-sm font-semibold">{title}</p>
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        </div>
      </div>
      <Switch checked={enabled} onCheckedChange={onToggle} data-testid={`${testId}-switch`} />
    </div>
    <div className={cn("space-y-4 p-4", !enabled && "opacity-60")}>{children}</div>
  </div>
);

/**
 * Pixel settings for a landing page: Meta, Google (GA4 + Ads), TikTok.
 * `pixel` is the page.pixel object; `set(key, value)` patches a single field.
 */
const PixelSettings = ({ pixel, set }) => (
  <div className="space-y-4" data-testid="pixel-settings">
    {/* Meta */}
    <PlatformCard title="Meta (Facebook & Instagram) Pixel" subtitle="PageView on load + your chosen event on CTA clicks." enabled={pixel.enabled} onToggle={(v) => set("enabled", v)} testId="pixel-meta" badge="f">
      <F label="Pixel ID" hint="Meta Events Manager → Data sources. Numbers only.">
        <Input value={pixel.meta_pixel_id} onChange={(e) => set("meta_pixel_id", e.target.value.replace(/[^0-9]/g, ""))} placeholder="123456789012345" className={inputCls} data-testid="editor-pixel-id" />
      </F>
      <F label="CTA click event">
        <Select value={pixel.cta_event} onValueChange={(v) => set("cta_event", v)}>
          <SelectTrigger className="h-10 rounded-none border-border bg-transparent" data-testid="editor-pixel-event">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-none border-border bg-card">
            {META_EVENTS.map((ev) => (
              <SelectItem key={ev} value={ev}>
                {ev}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </F>
      <F label="Advanced: paste full pixel code (optional)" hint="Paste the complete <script> snippet from Meta if you prefer. Injected on this landing page only.">
        <Textarea value={pixel.custom_code} onChange={(e) => set("custom_code", e.target.value)} rows={4} className={cn(areaCls, "min-h-[100px] font-mono text-xs")} placeholder="<script>!function(f,b,e,v,n,t,s)…</script>" data-testid="editor-pixel-code" />
      </F>
    </PlatformCard>

    {/* Google */}
    <PlatformCard title="Google Analytics 4" subtitle="page_view on load + cta_click event on CTA clicks." enabled={pixel.ga4_enabled} onToggle={(v) => set("ga4_enabled", v)} testId="pixel-ga4" badge="G">
      <F label="Measurement ID" hint="GA4 Admin → Data streams → Web. Looks like G-XXXXXXXXXX.">
        <Input value={pixel.ga4_measurement_id} onChange={(e) => set("ga4_measurement_id", e.target.value.trim().toUpperCase())} placeholder="G-XXXXXXXXXX" className={inputCls} data-testid="editor-ga4-id" />
      </F>
    </PlatformCard>

    <PlatformCard title="Google Ads" subtitle="Conversion fired on CTA clicks (remarketing tag loads on page view)." enabled={pixel.google_ads_enabled} onToggle={(v) => set("google_ads_enabled", v)} testId="pixel-gads" badge="Ads">
      <div className="grid gap-4 sm:grid-cols-2">
        <F label="Conversion ID" hint="Looks like AW-123456789.">
          <Input value={pixel.google_ads_id} onChange={(e) => set("google_ads_id", e.target.value.trim().toUpperCase())} placeholder="AW-123456789" className={inputCls} data-testid="editor-gads-id" />
        </F>
        <F label="Conversion label (optional)" hint="From the conversion action's tag setup.">
          <Input value={pixel.google_ads_conversion_label} onChange={(e) => set("google_ads_conversion_label", e.target.value.trim())} placeholder="AbCdEfGhIjK" className={inputCls} data-testid="editor-gads-label" />
        </F>
      </div>
    </PlatformCard>

    {/* TikTok */}
    <PlatformCard title="TikTok Pixel" subtitle="Page event on load + your chosen event on CTA clicks." enabled={pixel.tiktok_enabled} onToggle={(v) => set("tiktok_enabled", v)} testId="pixel-tiktok" badge="TT">
      <div className="grid gap-4 sm:grid-cols-2">
        <F label="Pixel ID" hint="TikTok Ads Manager → Assets → Events.">
          <Input value={pixel.tiktok_pixel_id} onChange={(e) => set("tiktok_pixel_id", e.target.value.trim())} placeholder="CXXXXXXXXXXXXXXXXX" className={inputCls} data-testid="editor-tiktok-id" />
        </F>
        <F label="CTA click event">
          <Select value={pixel.tiktok_cta_event} onValueChange={(v) => set("tiktok_cta_event", v)}>
            <SelectTrigger className="h-10 rounded-none border-border bg-transparent" data-testid="editor-tiktok-event">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none border-border bg-card">
              {TIKTOK_EVENTS.map((ev) => (
                <SelectItem key={ev} value={ev}>
                  {ev}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </F>
      </div>
    </PlatformCard>
  </div>
);

export default PixelSettings;
